import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MdArrowDownward, MdArrowUpward, MdHighlightOff } from "react-icons/md";
import {
  AbsoluteCenter,
  Box,
  Collapsible,
  Flex,
  Grid,
  IconButton,
  LinkBox,
  LinkOverlay,
  Skeleton,
  Table,
  Text,
} from "@chakra-ui/react";

import {
  PageContent,
  PageHeader,
  PageLayout,
  PageTitle,
} from "@/components/page";
import { EmptyState } from "@/components/ui/empty-state";
import {
  useObservationDates,
  useObservationStats,
  useTank,
  useTankStats,
  useTableView,
} from "@/hooks";
import { IRackStats } from "@/types/data";
import {
  BarChartBox,
  GrowthZoneBox,
  GrowthZoneCell,
  LineChartBox,
  TimespanFilter,
} from "@/components/charts";
import {
  ITimespan,
  TIMESPAN_OPTIONS,
  getTimespanInterval,
} from "@/components/charts/timespan";
import { MdPhotoCamera } from "react-icons/md";
import InsightCard from "@/components/InsightCard";
import ObservationsCalendar from "@/components/charts/calendar";
import PageSectionTitle from "@/components/page/PageSectionTitle";
import HealthIssueCell from "@/components/HealthIssueCell";
import SummaryCard from "@/components/SummaryCard";
import { CollapsibleTrigger } from "@/components";
import TableViewToggle from "@/components/TableViewToggle";

type RouteParams = {
  nurseryId: string;
  tankId: string;
};

function TankDetail() {
  const { nurseryId, tankId } = useParams<RouteParams>();
  const { view } = useTableView();
  const [open, setOpen] = useState(true);
  const { data, isPending, error } = useTank(nurseryId!, tankId!);
  const { data: stats, isPending: statsIsPending } = useTankStats(
    nurseryId!,
    tankId!,
  );
  const [timespan, setTimespan] = useState<ITimespan>("Month");
  const { data: obsDates, isPending: obsDatesIsPending } = useObservationDates(
    nurseryId!,
    new URLSearchParams({ rack__tank: tankId! }),
  );
  const { data: obsStats, isPending: obsStatsIsPending } = useObservationStats(
    nurseryId!,
    getTimespanInterval(timespan),
    new URLSearchParams({
      rack__tank: tankId!,
      created_after: TIMESPAN_OPTIONS[timespan],
    }),
  );

  return (
    <PageLayout>
      <PageHeader backLink={`/nurseries/${nurseryId}`}>
        Tank {data?.name}
      </PageHeader>
      <PageTitle tankName={data?.name} isPending={isPending} />
      <PageContent>
        {error && (
          <EmptyState
            icon={<MdHighlightOff />}
            title="Error"
            description={error.message}
          />
        )}
        <Collapsible.Root defaultOpen onOpenChange={(e) => setOpen(e.open)}>
          <Flex
            alignItems="end"
            w="full"
            justifyContent="space-between"
            mb={{ base: 4, sm: 6 }}
            flexWrap="wrap"
          >
            <TimespanFilter timespan={timespan} setTimespan={setTimespan} />
            <CollapsibleTrigger open={open} />
          </Flex>
          <Collapsible.Content>
            <Grid
              templateColumns="repeat(auto-fit, minmax(8rem, 1fr))"
              gap="2"
              mb="4"
            >
              <InsightCard label="Total Racks" value={stats?.length} />
              <InsightCard
                label="Total Fragments"
                value={stats?.reduce((acc, i) => (acc += i.fragments), 0)}
              />
              <InsightCard
                label="Average Growth"
                value={`${stats?.length ? (stats.reduce((acc, i) => (acc += i.growth), 0) / stats.length).toFixed(1) : "0"}%`}
              />
            </Grid>
          </Collapsible.Content>
        </Collapsible.Root>
        <Grid
          templateColumns={{
            base: "1fr",
            sm: "repeat(auto-fit, minmax(18rem, 1fr))",
            md: "20rem repeat(auto-fit, minmax(14rem, 1fr))",
          }}
          gap="2"
          mb="4"
        >
          <Skeleton loading={obsDatesIsPending}>
            <ObservationsCalendar
              activeDates={
                obsDates?.map((obs) =>
                  new Date(obs.date).toLocaleDateString(),
                ) || []
              }
            />
          </Skeleton>
          <Skeleton loading={obsStatsIsPending}>
            <LineChartBox
              title="Aggregate Tank Area"
              info="The area of all fragments over time"
              data={obsStats || []}
              interval={getTimespanInterval(timespan)}
            />
          </Skeleton>
          <Skeleton loading={obsStatsIsPending}>
            <BarChartBox
              title="Tank Health Issues"
              info="Most common health issues in the tank"
              data={obsStats || []}
            />
          </Skeleton>
          <Skeleton loading={statsIsPending}>
            {stats && (
              <GrowthZoneBox
                title="Tank Growth Zones"
                fragments_count_0_25={stats.reduce(
                  (acc, curr) => acc + curr.fragments_count_0_25,
                  0,
                )}
                fragments_count_25_50={stats.reduce(
                  (acc, curr) => acc + curr.fragments_count_25_50,
                  0,
                )}
                fragments_count_50_75={stats.reduce(
                  (acc, curr) => acc + curr.fragments_count_50_75,
                  0,
                )}
                fragments_count_75_100={stats.reduce(
                  (acc, curr) => acc + curr.fragments_count_75_100,
                  0,
                )}
              />
            )}
          </Skeleton>
        </Grid>

        <Flex
          mb="1"
          w="full"
          alignItems="center"
          justifyContent="space-between"
        >
          <PageSectionTitle>Racks</PageSectionTitle>
          <TableViewToggle />
        </Flex>
        {view === "list" && (
          <Table.Root
            interactive
            colorPalette="blue"
            variant="line"
            stickyHeader
          >
            <Table.Header
              textTransform="uppercase"
              fontSize="xs"
              letterSpacing="0.75px"
            >
              <Table.Row
                css={{ "& > *": { color: "gray.400", fontWeight: "semibold" } }}
              >
                <Table.ColumnHeader>Rack</Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm">
                  Last Observation
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="right">
                  # of Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader hideBelow="sm" textAlign="center">
                  Growth Zones
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Unhealthy Fragments
                </Table.ColumnHeader>
                <Table.ColumnHeader textAlign="right">
                  Growth
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {stats?.map(
                ({
                  rack,
                  fragments,
                  latest_observation_date,
                  unhealthy_fragments,
                  growth,
                  fragments_count_0_25,
                  fragments_count_25_50,
                  fragments_count_50_75,
                  fragments_count_75_100,
                }: IRackStats) => (
                  <LinkBox
                    key={rack.id}
                    position="relative"
                    as={Table.Row}
                    transform="scale(1)"
                    _hover={{ bg: "blue.50" }}
                  >
                    <Table.Cell fontWeight="bold" color="blue.500">
                      <LinkOverlay asChild>
                        <Link to={`racks/${rack.id}/`}>{rack.name}</Link>
                      </LinkOverlay>
                    </Table.Cell>
                    <Table.Cell hideBelow="sm">
                      {latest_observation_date &&
                        new Date(latest_observation_date).toLocaleString([], {
                          dateStyle: "short",
                          timeStyle: "short",
                        })}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm" textAlign="right">
                      {fragments}
                    </Table.Cell>
                    <Table.Cell hideBelow="sm">
                      <GrowthZoneCell
                        fragments_count_0_25={fragments_count_0_25}
                        fragments_count_25_50={fragments_count_25_50}
                        fragments_count_50_75={fragments_count_50_75}
                        fragments_count_75_100={fragments_count_75_100}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <HealthIssueCell
                        fragments={fragments}
                        unhealthy_fragments={unhealthy_fragments}
                      />
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      {growth !== null
                        ? `${Number(growth).toFixed(1).replace(/\.0$/, "")}%`
                        : "-"}
                      <Text display="inline-block" pl="1">
                        {growth > 0 && <MdArrowUpward />}
                        {growth < 0 && <MdArrowDownward />}
                      </Text>
                    </Table.Cell>
                  </LinkBox>
                ),
              )}
            </Table.Body>
          </Table.Root>
        )}
        {view === "grid" && (
          <Grid
            templateColumns={{
              base: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(3, 1fr)",
            }}
            gap={[2, 4]}
            mb="4"
          >
            {stats?.map((s: IRackStats) => (
              <SummaryCard
                key={s.rack.id}
                linkTo={`racks/${s.rack.id}/`}
                title={s.rack.name}
                fragments={s.fragments}
                unhealthyFragments={s.unhealthy_fragments}
                growth={s.growth}
                fragments_count_0_25={s.fragments_count_0_25}
                fragments_count_25_50={s.fragments_count_25_50}
                fragments_count_50_75={s.fragments_count_50_75}
                fragments_count_75_100={s.fragments_count_75_100}
              />
            ))}
          </Grid>
        )}
      </PageContent>
      <Box
        hideFrom="sm"
        zIndex="10"
        textAlign="center"
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        bg="bg.info"
        pt="6"
        h="12"
        borderTopWidth="1px"
        borderColor="blue.100"
      >
        <AbsoluteCenter>
          <IconButton
            bottom="6"
            asChild
            aria-label="Take photo"
            rounded="full"
            size="xl"
            colorPalette="blue"
            variant="surface"
          >
            <Link to="observation/">
              <MdPhotoCamera />
            </Link>
          </IconButton>
        </AbsoluteCenter>
        <Text fontSize="xs" color="blue.700">
          New Observation
        </Text>
        <Text fontSize="xs" color="blue.700">
          New Observation
        </Text>
      </Box>
    </PageLayout>
  );
}

export default TankDetail;
